import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Route, Redirect } from 'react-router-dom';
import { ConnectedRouter } from 'react-router-redux';
import { connect } from 'react-redux';
import { message, Modal } from 'antd';
import { CaretDown, CaretUp } from '@phosphor-icons/react';
import {
  getSessionUrgency,
  shouldReexpandImpersonationBanner,
  trackImpersonationBannerToggle,
} from './impersonationBanner';
import ReferralCountPreview from './ReferralCountPreview';

import {
  logout,
  validateAuth,
  getLoginInfo,
  redirectToCreateGuestAccount,
} from '../../actions/auth';
import { setNumberOfNewReferrals, incrementReferralCount, decrementReferralCount } from '../../actions/socket';
import {
  changeModalState,
  loginAdmin,
} from '../../actions/userProfile';
import { toggleSidebar, updateCurrentPath } from '../../actions/appActions';
import { openModal } from '../../actions/modal';
import { hasAuthCreds } from '../../reducers/auth';

import {
  userIsAuthenticatedRedir,
  userIsNotAuthenticatedRedir,
} from '../../global/auth';

import ConnectedSwitch from '../../components/utils/switch';
import ErrorDisplay from '../../components/viewer/errorDisplay';
import SplashScreen from '../../components/SplashScreen';
import BillingPrompt from '../../components/billing';
import HomeComponent from '../Home';
import LoginMicrosoftComponent from '../LoginMicrosoft';
import UploadedRecordsComponent from '../records/pages/UploadedRecords';
import ReceivedRecordsComponent from '../records/pages/InboxRedesign';
import AssignedRecordsComponent from '../records/pages/AssignedRecords';
import ReferralManagementComponent from '../records/pages/ReferralManagement';
import ChromeRecordSelector from '../ChromeRecordSelector';
import ContactsComponent from '../../components/redesign/pages/ContactsRedesign';
// ENG-3560: switches between the legacy Sent page and its redesign on `sent_redesign`.
import SentRecordsComponent from '../records/pages/SentRecordsRedesign';
import SentRecordStatusComponent from '../SentRecordStatus';
import LoginComponent from '../NewLogin/index';
import SignUpGuestComponent from '../SignUpGuest';
import PatientAccessComponent from '../PatientAccess';
import UserProfileComponent from '../../components/redesign/pages/UserProfile';
import AccessCodeComponent from './AccessCode/index';
import Error404 from '../404';
import ModalRoot from '../ModalRoot';
import ResetPasswordComponent from './ResetPassword/index';
import ForgotPasswordComponent from '../ForgotPassword/index';
import IntroToMedsenderComponent from '../walkthrough/IntroToMedsender';
import PrinterSignInComponent from '../PrinterSignIn';
import MedsenderGuideComponent from '../MedsenderGuide';
import AnalyticsComponent from '../Analytics';
import ReferralPortalComponent from '../portal/ReferralsList';
import PortalSettingsComponent from '../portal/PortalSettings';
import PortalSignupComponent from '../PortalSignup';
import Dragger, { ImpersonationBannerTab } from './style';
import { hasFeatureInLocalStorage, verifyFeatureAccess } from '../../global/featureFlags';
import MessageNotificationsComponent from '../records/pages/MessageNotifications';
import NewHeader from '../NewHeader';
import NewNav from '../NewNav';
import PortalTopBar from '../portal/PortalTopBar';
import VoiceDashboardComponent from '../Voice';
import ImpersonationSetup from '../ImpersonationSetup';
import clearImpersonationLocalStorage from '../../global/clearImpersonationLocalStorage';
import axios from 'axios';
import {
  initImpersonationChannel,
  onImpersonationChange,
  closeImpersonationChannel,
  isFromThisTab,
  broadcastImpersonationEnded,
  setupImpersonationTimeoutInterceptor,
} from '../../global/impersonationChannel';
import AiChat from '../AiChat';
import LearnMedsender from '../../components/tutorial/LearnMedsender';
import ViewerLoadBoundary from '../../components/viewer/ViewerLoadBoundary';
import StatusBannerStack from '../../components/StatusBannerStack';
import {
  loadRecordViewer,
  loadRecordViewerGuest,
  loadRecordViewerLink,
  loadRecordViewerPatient,
  scheduleAuthenticatedViewerPrefetch,
} from '../../utils/viewerImports';
import { shouldEnablePreloading } from '../../utils/mobileDetect';
import { buildAppContentStyle, buildAppShellStyle } from './layout';
import RecordViewerIframeComponent from '../RecordViewerIframe';
import { isPrinterDriverSupportedDevice } from '../../util/printerDriverDownload';

const PatientRecaptureComponent = React.lazy(() => import('../../components/PatientRecapture'));
const RecaptureSalesComponent = React.lazy(() => import('../../components/RecaptureSales'));
const ArmSalesComponent = React.lazy(() => import('../../components/ArmSales'));
const PatientEngagementSalesComponent = React.lazy(() => import('../../components/PatientEngagementSales'));
const PatientEngagementComponent = React.lazy(() => import('../../components/PatientEngagement'));
const RecordViewerComponent = React.lazy(loadRecordViewer);
const RecordViewerGuestComponent = React.lazy(loadRecordViewerGuest);
const RecordViewerLinkComponent = React.lazy(loadRecordViewerLink);
const RecordViewerPatientComponent = React.lazy(loadRecordViewerPatient);

const Home = userIsAuthenticatedRedir(HomeComponent);
const LoginMicrosoft = userIsAuthenticatedRedir(LoginMicrosoftComponent);
const Login = userIsNotAuthenticatedRedir(LoginComponent);
const AccessCode = userIsNotAuthenticatedRedir(AccessCodeComponent);
const UploadedRecords = userIsAuthenticatedRedir(UploadedRecordsComponent);
const ReceivedRecords = userIsAuthenticatedRedir(ReceivedRecordsComponent);
const AssignedRecords = userIsAuthenticatedRedir(AssignedRecordsComponent);
const ReferralManagement = userIsAuthenticatedRedir(ReferralManagementComponent);
const ChromeRecordSelectorPage = userIsAuthenticatedRedir(ChromeRecordSelector);
const SentRecords = userIsAuthenticatedRedir(SentRecordsComponent);
const SentRecordStatus = userIsAuthenticatedRedir(SentRecordStatusComponent);
const RecordViewer = userIsAuthenticatedRedir(RecordViewerComponent);
const RecordViewerIframe = userIsAuthenticatedRedir(RecordViewerIframeComponent);
const Contacts = userIsAuthenticatedRedir(ContactsComponent);
const RecordViewerGuest = userIsNotAuthenticatedRedir(RecordViewerGuestComponent);
const SignUpGuest = userIsNotAuthenticatedRedir(SignUpGuestComponent);
const PatientAccess = userIsNotAuthenticatedRedir(PatientAccessComponent);
const RecordViewerPatient = userIsNotAuthenticatedRedir(RecordViewerPatientComponent);
const UserProfile = userIsAuthenticatedRedir(UserProfileComponent);
const ResetPassword = userIsNotAuthenticatedRedir(ResetPasswordComponent);
const ForgotPassword = userIsNotAuthenticatedRedir(ForgotPasswordComponent);
const IntroToMedsender = userIsAuthenticatedRedir(IntroToMedsenderComponent);
const PrinterSignIn = userIsAuthenticatedRedir(PrinterSignInComponent);
const MedsenderGuide = userIsAuthenticatedRedir(MedsenderGuideComponent);
const MesssageNotifications = userIsAuthenticatedRedir(MessageNotificationsComponent);
const VoiceDashboard = userIsAuthenticatedRedir(VoiceDashboardComponent);
const Analytics = userIsAuthenticatedRedir(AnalyticsComponent);
const ReferralPortal = userIsAuthenticatedRedir(ReferralPortalComponent);
const PortalSettings = userIsAuthenticatedRedir(PortalSettingsComponent);
const PortalSignup = userIsNotAuthenticatedRedir(PortalSignupComponent);
const PatientRecapture = userIsAuthenticatedRedir(PatientRecaptureComponent);
const RecaptureSales = userIsAuthenticatedRedir(RecaptureSalesComponent);
const ArmSales = userIsAuthenticatedRedir(ArmSalesComponent);
const PatientEngagementSales = userIsAuthenticatedRedir(PatientEngagementSalesComponent);
const PatientEngagement = userIsAuthenticatedRedir(PatientEngagementComponent);
const lazyRouteFallback = <div style={{ padding: '48px', textAlign: 'center' }}>Loading...</div>;
const buildViewerResetKey = ({ location }) => `${location.pathname}${location.search || ''}`;
const renderSuspendedRoute = (renderRoute, fallback = lazyRouteFallback) => (props) => (
  <React.Suspense fallback={fallback}>
    {renderRoute(props)}
  </React.Suspense>
);

const renderViewerRoute = (ViewerComponent, extraProps = {}) => (props) => (
  <React.Suspense fallback={lazyRouteFallback}>
    <ViewerLoadBoundary resetKey={buildViewerResetKey(props)}>
      <ViewerComponent {...props} {...extraProps} />
    </ViewerLoadBoundary>
  </React.Suspense>
);

const MainRoutes = cable => (
  <ConnectedSwitch>
    <Route exact path="/app/impersonate/:id" component={ImpersonationSetup} />
    <Route
      exact
      path="/app"
      render={(props) => (
        <Home
          {...props}
          cable={cable}
        />
      )}
    />
    <Route exact path="/app/login_microsoft" component={LoginMicrosoft} />
    <Route exact path="/app/contacts" component={Contacts} />
    <Route exact path="/app/uploads" component={UploadedRecords} />
    <Route exact path="/app/sent" component={SentRecords} />
    <Route exact path="/app/received" component={ReceivedRecords} />
    <Route exact path="/app/assigned" component={AssignedRecords} />
    <Route exact path="/app/referred" component={ReferralManagement} />
    <Route exact path="/app/voice_inbox" component={VoiceDashboard} />
    <Route exact path="/app/analytics" component={Analytics} />
    <Route
      exact
      path="/app/referral_portal/shares/:shareId"
      render={({ match }) => (
        <Redirect to={`/app/referral_portal?share_token=${match.params.shareId}`} />
      )}
    />
    <Route exact path="/app/referral_portal/settings" render={props => <PortalSettings {...props} />} />
    <Route exact path="/app/referral_portal" render={props => <ReferralPortal {...props} cable={cable} />} />
    <Route exact path="/app/portal/signup" component={PortalSignup} />
    <Route
      path="/app/patient-recapture"
      render={renderSuspendedRoute(props => <PatientRecapture {...props} />)}
    />
    <Route
      path="/app/recapture"
      render={renderSuspendedRoute(props => <RecaptureSales {...props} />)}
    />
    <Route
      path="/app/arm"
      render={renderSuspendedRoute(props => <ArmSales {...props} />)}
    />
    <Route
      path="/app/engagement"
      render={renderSuspendedRoute(props => <PatientEngagementSales {...props} />)}
    />
    <Route
      path="/app/patient-engagement"
      render={renderSuspendedRoute(props => <PatientEngagement {...props} />)}
    />
    <Route exact path="/app/view/:id" render={renderViewerRoute(RecordViewer, { cable })} />
    <Route exact path="/app/records/status/record/:record_id/receiver/:id" component={SentRecordStatus} />
    <Route exact path="/app/user" component={UserProfile} />
    <Route exact path="/app/access_code" component={AccessCode} />
    <Route exact path="/app/access" render={renderViewerRoute(RecordViewerGuest)} />
    <Route exact path="/app/view/:record_id/link/:id" render={renderViewerRoute(RecordViewerLinkComponent)} />
    <Route exact path="/app/access/sign_up" component={SignUpGuest} />
    <Route exact path="/app/patient/:patient_id/record/:id" component={PatientAccess} />
    <Route exact path="/app/patient" component={PatientAccess} />
    <Route exact path="/app/patient_access" render={renderViewerRoute(RecordViewerPatient)} />
    <Route exact path="/app/chrome/received_selector" component={ChromeRecordSelectorPage} />
    <Route exact path="/app/chrome/uploaded_selector" component={ChromeRecordSelectorPage} />
    <Route exact path="/app/chrome/view/:id" render={renderViewerRoute(RecordViewerIframe)} />
    <Route exact path="/app/forgot_password" component={ForgotPassword} />
    <Route exact path="/app/walkthrough" component={IntroToMedsender} />
    <Route exact path="/app/printer_sign_in" component={PrinterSignIn} />
    <Route exact path="/app/guide" component={MedsenderGuide} />
    <Route exact path="/app/unread_messages" component={MesssageNotifications} />
    <Route component={Error404} />
  </ConnectedSwitch>
);

const userAuthenticated = hasAuthCreds();

const HandleIndex = () => {
  if (userAuthenticated === true) {
    return (<Redirect to="/app" />);
  }
  return (<Redirect to="/login" />);
};

class App extends Component {
  constructor() {
    super();
    this.state = {
      isBillingTracked: false,
      impersonationConflict: false,
      impersonationBannerHeight: 0,
      statusBannerHeight: 0,
      impersonationRemainingSeconds: null,
      impersonationBannerCollapsed: false,
    };
    this.appShellRef = React.createRef();
    this.impersonationBannerRef = React.createRef();
    this.impersonationBannerToggleRef = React.createRef();
    this.impersonationBannerTabRef = React.createRef();
    this.impersonationRefreshTimer = null;
    this.impersonationCountdownTimer = null;
    this.unsubscribeImpersonationChannel = null;
    this.trackBilling = this.trackBilling.bind(this);
    this.renderMainRoutes = this.renderMainRoutes.bind(this);
    this.measureImpersonationBanner = this.measureImpersonationBanner.bind(this);
    this.handleStatusBannerHeightChange = this.handleStatusBannerHeightChange.bind(this);
    this.stopImpersonating = this.stopImpersonating.bind(this);
    this.handleStopImpersonating = this.handleStopImpersonating.bind(this);
    this.handleImpersonationChange = this.handleImpersonationChange.bind(this);
    this.startImpersonationCountdown = this.startImpersonationCountdown.bind(this);
    this.stopImpersonationCountdown = this.stopImpersonationCountdown.bind(this);
    this.formatRemainingTime = this.formatRemainingTime.bind(this);
    this.toggleImpersonationBanner = this.toggleImpersonationBanner.bind(this);
  }

  componentWillMount() {
    this.props.validateAuth();
    this.props.history.listen((location) => {
      this.props.updateCurrentPath(location.pathname);
    });
  }

  componentDidMount() {
    // Inherit the shell's measured banner offsets so visible messages also move
    // when a banner expands or collapses, without covering the record toolbar.
    message.config({
      top: 'calc(var(--app-header-top-offset, 0px) + 8px)',
      maxCount: 3,
      getContainer: () => this.appShellRef.current || document.body,
    });

    if (hasAuthCreds() && shouldEnablePreloading()) {
      scheduleAuthenticatedViewerPrefetch();
    }

    if (this.props.impersonating) {
      this.startImpersonationRefresh();
      this.startImpersonationCountdown(this.props.impersonationExpiresIn);
    }

    // Listen for impersonation changes from other tabs
    initImpersonationChannel();
    this.unsubscribeImpersonationChannel = onImpersonationChange(this.handleImpersonationChange);

    // Setup axios interceptor to display impersonation timeout messages from JSON responses
    setupImpersonationTimeoutInterceptor(axios);

    window.addEventListener('resize', this.measureImpersonationBanner);
    this.measureImpersonationBanner();
  }

  componentDidUpdate(prevProps) {
    setTimeout(() => {
      this.trackBilling();
    });

    if (this.props.impersonating && !prevProps.impersonating) {
      // A new impersonation session must never inherit a collapsed banner
      // from a previous session in the same page lifetime.
      this.setState({ impersonationBannerCollapsed: false });
      this.startImpersonationRefresh();
      this.startImpersonationCountdown(this.props.impersonationExpiresIn);
    } else if (!this.props.impersonating && prevProps.impersonating) {
      this.stopImpersonationRefresh();
      this.stopImpersonationCountdown();
    } else if (
      this.props.impersonating
      && this.props.impersonationExpiresIn !== prevProps.impersonationExpiresIn
      && this.props.impersonationExpiresIn != null
    ) {
      // Session was refreshed, update the countdown with fresh expiry time
      this.startImpersonationCountdown(this.props.impersonationExpiresIn);
    }

    this.measureImpersonationBanner();
  }

  componentWillUnmount() {
    message.destroy();
    message.config({ top: 8, getContainer: () => document.body });
    window.removeEventListener('resize', this.measureImpersonationBanner);
    this.stopImpersonationRefresh();
    this.stopImpersonationCountdown();
    if (this.unsubscribeImpersonationChannel) {
      this.unsubscribeImpersonationChannel();
    }
    closeImpersonationChannel();
  }

  measureImpersonationBanner() {
    const banner = this.impersonationBannerRef.current;
    const impersonationBannerHeight = banner ? Math.ceil(banner.getBoundingClientRect().height) : 0;

    if (impersonationBannerHeight !== this.state.impersonationBannerHeight) {
      this.setState({ impersonationBannerHeight });
    }
  }

  handleStatusBannerHeightChange(statusBannerHeight) {
    if (statusBannerHeight !== this.state.statusBannerHeight) {
      this.setState({ statusBannerHeight });
    }
  }

  startImpersonationRefresh() {
    if (this.impersonationRefreshTimer) return;
    this.consecutiveRefreshFailures = 0;

    const doRefresh = () => {
      if (this.isImpersonationSetupRoute()) return;

      Promise.resolve(this.props.getLoginInfo({ rejectOnError: true }))
        .then(() => {
          // Reset failure counter on success
          this.consecutiveRefreshFailures = 0;
        })
        .catch((error) => {
          // Track consecutive failures and notify user
          this.consecutiveRefreshFailures = (this.consecutiveRefreshFailures || 0) + 1;

          // Check for auth failure (session expired)
          const status = error?.response?.status;
          if (status === 401 || status === 403) {
            message.error(
              'Impersonation session expired. Please close this tab and start a new impersonation session.',
              0, // Don't auto-dismiss
            );
            this.stopImpersonationRefresh();
            return;
          }

          // After 3 consecutive failures, show warning
          if (this.consecutiveRefreshFailures >= 3) {
            message.warning(
              'Unable to refresh impersonation session. Your session may have expired.',
              10,
            );
          }
        });
    };

    // Immediate refresh to fetch impersonator email and session details
    doRefresh();
    this.impersonationRefreshTimer = window.setInterval(doRefresh, 5 * 60 * 1000);
  }

  stopImpersonationRefresh() {
    if (!this.impersonationRefreshTimer) return;
    window.clearInterval(this.impersonationRefreshTimer);
    this.impersonationRefreshTimer = null;
  }

  startImpersonationCountdown(initialSeconds) {
    this.stopImpersonationCountdown();

    if (initialSeconds == null || initialSeconds <= 0) return;

    // The periodic session refresh restarts the countdown with server truth,
    // which can jump across urgency buckets in one step (especially in
    // timer-throttled background tabs) — so this path needs the same
    // re-expand check as the 1s tick.
    this.setState((prevState) => {
      const update = { impersonationRemainingSeconds: initialSeconds };
      if (shouldReexpandImpersonationBanner({
        collapsed: prevState.impersonationBannerCollapsed,
        prevSeconds: prevState.impersonationRemainingSeconds,
        nextSeconds: initialSeconds,
      })) {
        update.impersonationBannerCollapsed = false;
      }
      return update;
    });

    this.impersonationCountdownTimer = window.setInterval(() => {
      this.setState((prevState) => {
        const newSeconds = (prevState.impersonationRemainingSeconds || 0) - 1;
        if (newSeconds <= 0) {
          this.stopImpersonationCountdown();
          // Auto-stop impersonation server-side when countdown expires. Also
          // resurface the banner so an aborted stop can't strand the user on
          // an expired session with no visible indicator.
          this.stopImpersonating();
          return { impersonationRemainingSeconds: 0, impersonationBannerCollapsed: false };
        }
        const update = { impersonationRemainingSeconds: newSeconds };
        if (shouldReexpandImpersonationBanner({
          collapsed: prevState.impersonationBannerCollapsed,
          prevSeconds: prevState.impersonationRemainingSeconds,
          nextSeconds: newSeconds,
        })) {
          update.impersonationBannerCollapsed = false;
        }
        return update;
      });
    }, 1000);
  }

  stopImpersonationCountdown() {
    if (!this.impersonationCountdownTimer) return;
    window.clearInterval(this.impersonationCountdownTimer);
    this.impersonationCountdownTimer = null;
  }

  toggleImpersonationBanner() {
    this.setState((prevState) => ({
      impersonationBannerCollapsed: !prevState.impersonationBannerCollapsed,
    }), () => {
      const { impersonationBannerCollapsed } = this.state;
      trackImpersonationBannerToggle(impersonationBannerCollapsed);
      // Keep keyboard focus on the counterpart control: the clicked button
      // is hidden (collapse) or unmounted (expand) by the toggle, which
      // would otherwise drop focus to <body>.
      const counterpart = impersonationBannerCollapsed
        ? this.impersonationBannerTabRef.current
        : this.impersonationBannerToggleRef.current;
      if (counterpart) counterpart.focus();
    });
  }

  isImpersonationSetupRoute() {
    return window.location.pathname.startsWith('/app/impersonate/');
  }

  formatRemainingTime(seconds) {
    if (seconds == null || seconds <= 0) return null;

    const minutes = Math.floor(seconds / 60);
    if (minutes >= 60) {
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;
      if (remainingMinutes === 0) {
        return `${hours}h`;
      }
      return `${hours}h ${remainingMinutes}m`;
    }
    if (minutes > 0) {
      return `${minutes} min`;
    }
    return `${seconds}s`;
  }

  // Checks if there's potentially unsaved work in form fields.
  // Returns true if any text input, textarea, or contenteditable has user-entered content.
  hasUnsavedWork() {
    // Check for non-empty text inputs and textareas
    const inputs = document.querySelectorAll('input[type="text"], input[type="email"], input[type="search"], input:not([type]), textarea');
    for (let i = 0; i < inputs.length; i += 1) {
      const input = inputs[i];
      // Skip hidden, disabled, or readonly fields
      if (input.type === 'hidden' || input.disabled || input.readOnly) continue;
      // Skip search/filter inputs (common patterns)
      if (input.placeholder?.toLowerCase().includes('search') || input.placeholder?.toLowerCase().includes('filter')) continue;
      // Check if field has content that wasn't there on page load (defaultValue)
      if (input.value && input.value !== input.defaultValue) {
        return true;
      }
    }

    // Check contenteditable elements (rich text editors)
    const editables = document.querySelectorAll('[contenteditable="true"]');
    for (let i = 0; i < editables.length; i += 1) {
      if (editables[i].textContent?.trim()) {
        return true;
      }
    }

    return false;
  }

  handleImpersonationChange(type, data) {
    // Ignore messages from this tab
    if (isFromThisTab(data)) return;

    const performReload = () => {
      window.location.reload();
    };

    const showReloadConfirmation = (title, content) => {
      Modal.confirm({
        title,
        content,
        okText: 'Reload Now',
        cancelText: 'Wait',
        onOk: performReload,
        onCancel: () => {
          message.warning('Please save your work and refresh the page to sync with the new session state.', 5);
        },
      });
    };

    if (type === 'IMPERSONATION_STARTED') {
      // Another tab started impersonating - force reload ALL tabs to sync with
      // the new server session state. This ensures frontend state matches the
      // session cookie which is now in impersonation mode.
      this.setState({ impersonationConflict: true });

      if (this.hasUnsavedWork()) {
        showReloadConfirmation(
          'Impersonation Started',
          'Impersonation started in another tab. You may have unsaved work on this page. Would you like to reload now or save your work first?',
        );
      } else {
        message.warning(
          'Impersonation started in another tab. Reloading to sync context...',
          3,
        );
        // Give user a moment to see the message, then reload
        setTimeout(performReload, 1500);
      }
    } else if (type === 'IMPERSONATION_ENDED') {
      // Another tab ended impersonation - force reload to reset all Redux state
      this.setState({ impersonationConflict: false });

      if (this.hasUnsavedWork()) {
        showReloadConfirmation(
          'Impersonation Ended',
          'Impersonation ended in another tab. You may have unsaved work on this page. Would you like to reload now or save your work first?',
        );
      } else {
        message.info('Impersonation ended in another tab. Reloading...', 3);
        setTimeout(performReload, 1500);
      }
    }
  }

  trackBilling() {
    if (this.props.impersonating) return;
    const { user } = this.props;
    const { isBillingTracked } = this.state;
    const isAuthenticated = user !== null;
    const { profitwell } = window;
    const { isAdmin, intercomHmac, currentOrganizationId } = this.props;

    if (isAuthenticated && !isBillingTracked) {
      const customerPaymentId = localStorage.getItem('customer_payment_id');

      let email = localStorage.getItem('email') || localStorage.getItem('m8_uid') || user.uid;
      if (email === 'undefined') {
        // Sometimes local storage has the value undefined. if so let's use the user uid attribute.
        email = user.uid;
      }

      let name = localStorage.getItem('m8_name') || user.fullName;
      if (name === 'undefined') {
        // Sometimes local storage has the value undefined. if so let's use the user uid attribute.
        name = user.fullName;
      }
      const customerPaymentIdExists = customerPaymentId && customerPaymentId !== 'null' && customerPaymentId !== 'undefined';

      if (name && intercomHmac) {
        const userFlags = JSON.parse(localStorage.getItem('flags'));

        let { autoUploadEmr } = user;
        if (autoUploadEmr === 'null') {
          autoUploadEmr = '';
        }

        const userData = {
          api_base: 'https://api-iam.intercom.io',
          app_id: process.env.INTERCOM_APP_ID,
          name: user.fullName,
          email,
          user_hash: intercomHmac,
          organization: user.organization,
          plan_type: localStorage.getItem('m8_plan_type'),
          emr: autoUploadEmr || user.emr,
          validEMRCredentials: !!autoUploadEmr,
          isAdmin,
        };
        if (currentOrganizationId && user.organization) {
          // Associate the user with an Intercom Company (auto-created on first boot).
          // Keyed by org ID so renaming an org updates the company instead of forking it.
          userData.company = {
            company_id: String(currentOrganizationId),
            name: user.organization,
          };
          if (customerPaymentIdExists) {
            // Custom company attribute linking to the org's Chargebee customer page.
            userData.company.chargebee_customer_url = `https://medsender.chargebee.com/d/customers/${customerPaymentId}`;
          }
        }
        if (userFlags && userFlags.data) {
          userFlags.data.flags.forEach((flag) => {
            if (flag.name === 'referral_mgmt_trial' && flag.active && flag.expiration) {
              // If the flag is referral mgmt trial and there is an expiry, include
              // if trial is expired or not.
              const expiration = Date.parse(flag.expiration);
              userData.referralMgmtTrialExpired = expiration < new Date();
            }
            userData[flag.name] = flag.active;
          });
        }
        window.Intercom('boot', userData);
      }

      if ((customerPaymentIdExists || email) && profitwell) {
        profitwell('user_id', customerPaymentIdExists ? customerPaymentId : email);
        this.setState({ isBillingTracked: true });
      }
    }
    if (!isAuthenticated && isBillingTracked && profitwell) {
      profitwell('user_id', null);
      this.setState({ isBillingTracked: false });
    }
  }

  logout() {
    window.Intercom('shutdown');
    this.props.logout();
  }

  clearM8LocalStorage() {
    try {
      clearImpersonationLocalStorage();
    } catch (e) {
      // If localStorage is unavailable, proceed; the server-side session still ends impersonation.
    }
  }

  handleStopImpersonating() {
    if (window.confirm('Stop impersonating? You will be returned to the admin panel.')) {
      this.stopImpersonating();
    }
  }

  stopImpersonating() {
    const csrfToken = document.getElementsByName('csrf-token')[0]?.content;
    if (!csrfToken) {
      message.error('Unable to stop impersonation. Please refresh the page and try again.');
      return;
    }

    this.clearM8LocalStorage();
    broadcastImpersonationEnded();

    const form = document.createElement('form');
    form.method = 'POST';
    form.action = '/admin/stop_impersonating';

    const tokenInput = document.createElement('input');
    tokenInput.type = 'hidden';
    tokenInput.name = 'authenticity_token';
    tokenInput.value = csrfToken;
    form.appendChild(tokenInput);

    document.body.appendChild(form);
    form.submit();
  }

  renderMainRoutes() {
    let currentDroppedFiles = [];
    const hasPrinterDriver = isPrinterDriverSupportedDevice();
    const onDropFiles = async (info) => {
      const canUpload = await verifyFeatureAccess('upload_record');
      const modal = canUpload ? 'UPLOAD_RECORD' : 'RAISE_FLAG';
      const droppedFileList = info?.fileList?.filter((file) => currentDroppedFiles.some((droppedFile) => droppedFile.uid === file.uid));
      currentDroppedFiles = [];

      const newFileList = droppedFileList.filter((file) => file.type.includes('pdf'));

      // Error for when a non-pdf file is dragged and dropped
      const errorText = hasPrinterDriver ? 'Only PDF files can be dragged. You can use the print driver to upload non-PDF files.' : 'Only PDF files can be uploaded';
      if (newFileList.length !== droppedFileList.length) {
        message.destroy();
        message.error({
          content: errorText,
        });
      }

      if (!newFileList.length) return;

      const modalType = {
        type: modal,
        ...(canUpload ? { data: { fileList: newFileList } } : { featureFlag: 'upload_record' }),
      };
      this.props.openModal(modalType);
    };

    return (
      <Dragger
        multiple
        onChange={onDropFiles}
        maxCount={5}
        beforeUpload={(file) => { currentDroppedFiles.push(file); return false; }}
        previewFile={false}
        openFileDialogOnClick={false}
        showUploadList={false}
        disabled={this.props.user === null}
      >
        {MainRoutes(this.props.cable)}
      </Dragger>
    );
  }

  render() {
    const pagesToHideNavBar = ['/app/login_microsoft', '/app/forgot_password', '/app/access_code', '/reset_password'];
    const isImpersonationSetupRoute = this.isImpersonationSetupRoute();
    const onMobile = window.innerWidth < 800;
    const showImpersonationBanner = this.props.user !== null
      && this.props.impersonating === true
      && !isImpersonationSetupRoute
      && !pagesToHideNavBar.includes(window.location.pathname);
    // Only redirect to referral portal if BOTH no_features AND referral_portal flags are present.
    // If no_features is set without referral_portal, user would be trapped with no accessible features.
    const hasNoFeatures = hasFeatureInLocalStorage('no_features');
    const hasReferralPortal = hasFeatureInLocalStorage('referral_portal');
    const isReferralPortalPath = window.location.pathname.startsWith('/app/referral_portal');
    const shouldForceReferralPortal = this.props.user !== null && hasNoFeatures && hasReferralPortal && !isReferralPortalPath;
    // On a hard load of a portal sub-page the auth wrapper can bounce through
    // /login?redirect=<portal path> before the session restores; honor that
    // param so the force-redirect preserves deep links like portal settings.
    const loginRedirectParam = window.location.pathname.startsWith('/login')
      ? new URLSearchParams(window.location.search).get('redirect')
      : null;
    const forcedPortalPath = (loginRedirectParam && loginRedirectParam.startsWith('/app/referral_portal'))
      ? loginRedirectParam
      : '/app/referral_portal';
    const isExternalPartner = this.props.user !== null && hasNoFeatures && hasReferralPortal;
    const { impersonationBannerCollapsed } = this.state;
    // A collapsed banner slides out of view, so the layout offset it normally
    // reserves must be released for the app content to reflow upward.
    const appShellStyle = buildAppShellStyle({
      showImpersonationBanner: showImpersonationBanner && !impersonationBannerCollapsed,
      impersonationBannerHeight: this.state.impersonationBannerHeight,
      statusBannerHeight: this.state.statusBannerHeight,
    });
    const appContentStyle = buildAppContentStyle({
      user: this.props.user,
      pathname: window.location.pathname,
      onMobile,
      isImpersonationSetupRoute,
    });
    const {
      error,
      errorCode,
      errorMessage,
      loading,
      user,
    } = this.props;

    // Session expiry styling based on urgency
    const { impersonationRemainingSeconds } = this.state;
    const sessionUrgency = getSessionUrgency(impersonationRemainingSeconds);
    const formattedRemainingTime = this.formatRemainingTime(impersonationRemainingSeconds);

    // Banner colors based on urgency level
    const bannerStyles = {
      normal: {
        background: '#fff3cd',
        borderColor: '#ffeeba',
        textColor: '#664d03',
        buttonBg: '#664d03',
      },
      warning: {
        background: '#fff3cd',
        borderColor: '#ffc107',
        textColor: '#664d03',
        buttonBg: '#664d03',
      },
      urgent: {
        background: '#f8d7da',
        borderColor: '#f5c6cb',
        textColor: '#721c24',
        buttonBg: '#721c24',
      },
    };
    const currentBannerStyle = bannerStyles[sessionUrgency] || bannerStyles.normal;

    // Post-auth canary↔stable redirect splash. The sentinel was set by
    // redirectIfDeploymentMismatch() in actions/auth.js before the hard
    // navigation, so we render the branded overlay until the destination
    // hydrates auth state (or the safety timeout fires). This is the
    // universal fallback path for SSO + any flow that does not benefit
    // from PR2's email preflight.
    const authReady = !loading && (user !== null || this.props.error);

    return (
      <ConnectedRouter history={this.props.history} cable={this.props.cable}>
        <React.Fragment>
          <>
            <SplashScreen authReady={authReady} />
            {shouldForceReferralPortal && <Redirect to={forcedPortalPath} />}
            <div ref={this.appShellRef} className="app-shell" style={appShellStyle}>
              {(!pagesToHideNavBar.includes(window.location.pathname) && !isImpersonationSetupRoute)
              && (
                <>
                  {showImpersonationBanner && (
                    <div
                      ref={this.impersonationBannerRef}
                      role="alert"
                      aria-live="assertive"
                      aria-hidden={impersonationBannerCollapsed}
                      style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        zIndex: 2000,
                        background: currentBannerStyle.background,
                        borderBottom: `1px solid ${currentBannerStyle.borderColor}`,
                        color: currentBannerStyle.textColor,
                        padding: '8px 12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        fontSize: '14px',
                        transform: impersonationBannerCollapsed ? 'translateY(-100%)' : 'translateY(0)',
                        // Delay the visibility flip until the slide-up finishes so the
                        // hidden banner also leaves the tab order; expanding is instant.
                        visibility: impersonationBannerCollapsed ? 'hidden' : 'visible',
                        transition: impersonationBannerCollapsed
                          ? 'transform 0.2s ease, visibility 0s linear 0.2s'
                          : 'transform 0.2s ease, visibility 0s',
                      }}
                    >
                      <div style={{ paddingRight: '12px', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                        <span>
                          Impersonating <strong>{this.props.userEmail}</strong>. Actions are attributed to support.
                        </span>
                        <ReferralCountPreview key={`${this.props.userEmail}:${this.props.currentOrganizationId}`} />
                        {formattedRemainingTime && (
                          <span
                            style={{
                              fontWeight: sessionUrgency === 'urgent' ? 'bold' : 'normal',
                              padding: '2px 8px',
                              borderRadius: '4px',
                              background: sessionUrgency === 'urgent' ? '#721c24' : (sessionUrgency === 'warning' ? '#664d03' : 'rgba(0,0,0,0.1)'),
                              color: sessionUrgency === 'normal' ? currentBannerStyle.textColor : 'white',
                              fontSize: '12px',
                            }}
                          >
                            {sessionUrgency === 'urgent' && 'Session expires in '}
                            {sessionUrgency === 'warning' && 'Expires in '}
                            {sessionUrgency === 'normal' && 'Expires in '}
                            {formattedRemainingTime}
                          </span>
                        )}
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          flexShrink: 0,
                        }}
                      >
                        <button
                          type="button"
                          onClick={this.handleStopImpersonating}
                          aria-label={`Stop impersonating ${this.props.userEmail}`}
                          style={{
                            background: currentBannerStyle.buttonBg,
                            color: 'white',
                            border: 'none',
                            padding: '6px 10px',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '13px',
                            flexShrink: 0,
                          }}
                        >
                          Stop impersonating
                        </button>
                        <button
                          type="button"
                          ref={this.impersonationBannerToggleRef}
                          onClick={this.toggleImpersonationBanner}
                          aria-label="Hide impersonation banner"
                          title="Hide impersonation banner"
                          style={{
                            background: 'transparent',
                            color: currentBannerStyle.textColor,
                            border: `1px solid ${currentBannerStyle.buttonBg}`,
                            padding: '4px 6px',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            flexShrink: 0,
                          }}
                        >
                          <CaretUp size={14} weight="bold" />
                        </button>
                      </div>
                    </div>
                  )}
                  {showImpersonationBanner && impersonationBannerCollapsed && (
                    <ImpersonationBannerTab
                      type="button"
                      ref={this.impersonationBannerTabRef}
                      onClick={this.toggleImpersonationBanner}
                      aria-label={`Show impersonation banner. Impersonating ${this.props.userEmail}`}
                      title={`Impersonating ${this.props.userEmail} — click to show banner`}
                      style={{
                        background: currentBannerStyle.background,
                        color: currentBannerStyle.textColor,
                        border: `1px solid ${currentBannerStyle.borderColor}`,
                      }}
                    >
                      <CaretDown size={14} weight="bold" />
                    </ImpersonationBannerTab>
                  )}
                  <StatusBannerStack
                    authenticated={this.props.user !== null}
                    cable={this.props.cable}
                    onHeightChange={this.handleStatusBannerHeightChange}
                  />
                  {isExternalPartner ? <PortalTopBar /> : <NewHeader cable={this.props.cable} />}
                  <NewNav
                    auth={this.props.user !== null}
                    guest={this.props.guest === true}
                    createGuest={this.props.createGuest === true}
                    user={this.props.user}
                    logout={this.logout.bind(this)}
                    docked={this.props.docked}
                    toggle={this.props.toggleSidebar}
                    openModal={this.props.openModal}
                    patient={this.props.patient}
                    patientAuth={this.props.patientAuthenticated === true}
                    faxSource={this.props.fax.source}
                    redirectToCreateGuestAccount={this.props.redirectToCreateGuestAccount}
                    billingTrialDetails={this.props.billingTrialDetails}
                    loginErrorMessage={this.props.loginErrorMessage}
                    adminLoginSuccess={this.props.adminLoginSuccess}
                    loginAdmin={this.props.loginAdmin}
                    isAdmin={this.props.isAdmin}
                    cable={this.props.cable}
                    setNumberOfNewReferrals={this.props.setNumberOfNewReferrals}
                    incrementReferralCount={this.props.incrementReferralCount}
                    decrementReferralCount={this.props.decrementReferralCount}
                    newReferralsCount={this.props.newReferralsCount}
                    history={this.props.history}
                    referralManagement={this.props.referralManagement}
                    viewReferralAnalytics={this.props.viewReferralAnalytics}
                  />
                </>
              )}
              <div className="app-content" style={appContentStyle}>
                {!isImpersonationSetupRoute && <ModalRoot />}
                { /* TODO: Add billing for lockout */ }
                { (!this.props.billing && this.props.user !== null && !isImpersonationSetupRoute)
                && (
                  <BillingPrompt
                    modalState={this.props.billingModalState}
                    modalOpen={this.props.billingModalOpen}
                    changeModal={this.props.changeModalState}
                    billingTrialDetails={this.props.billingTrialDetails}
                    planType={this.props.billingTrialDetails && this.props.billingTrialDetails.plan_type}
                    logout={this.logout.bind(this)}
                  />
                )}
                { error && !loading && user
                  ? (
                    <div style={{ textAlign: 'center' }}>
                      <ErrorDisplay message={errorMessage} error={errorCode} />
                    </div>
                  ) : (
                    <ConnectedSwitch>
                      <Route exact path="/" render={HandleIndex} />
                      <Route path="/app" component={this.renderMainRoutes} />
                      <Route exact path="/login" component={Login} />
                      <Route exact path="/reset_password" component={ResetPassword} />
                      <Route component={Error404} />
                    </ConnectedSwitch>
                  )
                }
                {user !== null && !onMobile ? <AiChat cable={this.props.cable} /> : null}
                {/* Guided tutorials: mounted app-wide so the account-menu entry
                    works from any screen and the post-walkthrough auto-open can
                    fire wherever the walkthrough left the user. */}
                {user !== null ? <LearnMedsender /> : null}
              </div>
            </div>
          </>
        </React.Fragment>
      </ConnectedRouter>
    );
  }
}

App.propTypes = {
  logout: PropTypes.func.isRequired,
  toggleSidebar: PropTypes.func.isRequired,
  redirectToCreateGuestAccount: PropTypes.func.isRequired,
  history: PropTypes.object.isRequired,
  validateAuth: PropTypes.func.isRequired,
  getLoginInfo: PropTypes.func.isRequired,
  user: PropTypes.object,
  isTrialUser: PropTypes.bool.isRequired,
  guest: PropTypes.bool.isRequired,
  createGuest: PropTypes.bool.isRequired,
  docked: PropTypes.bool.isRequired,
  openModal: PropTypes.func.isRequired,
  patient: PropTypes.bool.isRequired,
  patientAuthenticated: PropTypes.bool.isRequired,
  loginAdmin: PropTypes.func.isRequired,
  loginErrorMessage: PropTypes.string.isRequired,
  adminLoginSuccess: PropTypes.bool.isRequired,
  isAdmin: PropTypes.bool.isRequired,
  error: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
  errorCode: PropTypes.string,
  errorMessage: PropTypes.string,
  setNumberOfNewReferrals: PropTypes.func.isRequired,
  incrementReferralCount: PropTypes.func.isRequired,
  decrementReferralCount: PropTypes.func.isRequired,
  newReferralsCount: PropTypes.number.isRequired,
  cable: PropTypes.object.isRequired,
  intercomHmac: PropTypes.string.isRequired,
  referralManagement: PropTypes.bool.isRequired,
  viewReferralAnalytics: PropTypes.bool.isRequired,
  // Nullable: null during hydration before the auth bootstrap resolves. Used for the Intercom company_id.
  currentOrganizationId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  impersonating: PropTypes.bool.isRequired,
  impersonationExpiresIn: PropTypes.number,
  userEmail: PropTypes.string,
};

App.defaultProps = {
  currentOrganizationId: null,
  userEmail: '',
};

export default connect((state) => ({
  user: state.auth.data,
  isTrialUser: state.auth.trialUser,
  fax: state.auth.fax,
  userEmail: state.userProfile.email,
  isAdmin: state.auth.isAdmin,
  guest: state.auth.guest,
  createGuest: state.auth.createGuest,
  patient: state.auth.patient,
  intercomHmac: state.auth.intercomHmac,
  billing: state.auth.billingOnFile,
  billingTrialDetails: state.auth.billing,
  billingModalState: state.userProfile.modalState,
  billingModalOpen: state.userProfile.modalOpen,
  docked: state.appState.sidebarVisible,
  patientAuthenticated: state.auth.patientAuthenticated,
  loginErrorMessage: state.userProfile.loginErrorMessage,
  adminLoginSuccess: state.userProfile.loginSuccess,
  error: state.appState.error,
  errorMessage: state.appState.errorMessage,
  errorCode: state.appState.errorCode,
  loading: state.appState.isLoading,
  newReferralsCount: state.socket.newReferralsCount,
  referralManagement: state.auth.referralManagement,
  viewReferralAnalytics: state.auth.viewReferralAnalytics,
  currentOrganizationId: state.auth.currentOrganizationId,
  impersonating: state.auth.impersonating,

  impersonationExpiresIn: state.auth.impersonationExpiresIn,
}), {
  logout,
  validateAuth,
  getLoginInfo,
  toggleSidebar,
  openModal,
  redirectToCreateGuestAccount,
  changeModalState,
  setNumberOfNewReferrals,
  incrementReferralCount,
  decrementReferralCount,
  loginAdmin,
  updateCurrentPath,
})(App);
